import { ISurveyPermissions } from '@ViewModels';
import { useUserSession } from './appStateHooks';

/**
 * Hook to check survey permissions for the current user
 * Handles per-type permissions with fallback to top-level permissions
 */
export const useSurveyPermissions = () => {
	const userSession = useUserSession();
	const userGroupPermissions = userSession?.user?.userGroupPermissions;
	const surveyPermissions = userGroupPermissions?.featurePermissions?.survey as ISurveyPermissions | undefined;

	/**
	 * Check if user has read permission for a specific survey type
	 * Falls back to top-level read permission if type-specific is not defined
	 */
	const canReadSurveyType = (
		surveyType: 'eventRegistrationSurvey' | 'satisfactionSurvey' | 'customSurvey' | 'donationSurvey'
	): boolean => {
		if (!surveyPermissions) return false;

		// Check type-specific permission first
		const typePermission = surveyPermissions[surveyType];
		if (typePermission !== undefined && typePermission !== null) {
			return typePermission.read || false;
		}

		// Fallback to top-level permission
		return surveyPermissions.read || false;
	};

	/**
	 * Check if user has write permission for a specific survey type
	 * Falls back to top-level write permission if type-specific is not defined
	 */
	const canWriteSurveyType = (
		surveyType: 'eventRegistrationSurvey' | 'satisfactionSurvey' | 'customSurvey' | 'donationSurvey'
	): boolean => {
		if (!surveyPermissions) return false;

		// Check type-specific permission first
		const typePermission = surveyPermissions[surveyType];
		if (typePermission !== undefined && typePermission !== null) {
			return typePermission.write || false;
		}

		// Fallback to top-level permission
		return surveyPermissions.write || false;
	};

	/**
	 * Check if user has view reporting permission for a specific survey type
	 * Falls back to top-level viewReporting permission if type-specific is not defined
	 */
	const canViewReportingSurveyType = (
		surveyType: 'eventRegistrationSurvey' | 'satisfactionSurvey' | 'customSurvey' | 'donationSurvey'
	): boolean => {
		if (!surveyPermissions) return false;

		// Check type-specific permission first
		const typePermission = surveyPermissions[surveyType];
		if (typePermission !== undefined && typePermission !== null) {
			return typePermission.viewReporting || false;
		}

		// Fallback to top-level permission
		return surveyPermissions.viewReporting || false;
	};

	// Convenience methods for each survey type
	const canReadEventRegistrationSurvey = () => canReadSurveyType('eventRegistrationSurvey');
	const canReadSatisfactionSurvey = () => canReadSurveyType('satisfactionSurvey');
	const canReadCustomSurvey = () => canReadSurveyType('customSurvey');
	const canReadDonationSurvey = () => canReadSurveyType('donationSurvey');

	const canWriteEventRegistrationSurvey = () => canWriteSurveyType('eventRegistrationSurvey');
	const canWriteSatisfactionSurvey = () => canWriteSurveyType('satisfactionSurvey');
	const canWriteCustomSurvey = () => canWriteSurveyType('customSurvey');
	const canWriteDonationSurvey = () => canWriteSurveyType('donationSurvey');

	const canViewReportingEventRegistrationSurvey = () => canViewReportingSurveyType('eventRegistrationSurvey');
	const canViewReportingSatisfactionSurvey = () => canViewReportingSurveyType('satisfactionSurvey');
	const canViewReportingCustomSurvey = () => canViewReportingSurveyType('customSurvey');
	const canViewReportingDonationSurvey = () => canViewReportingSurveyType('donationSurvey');

	// Check if user has any survey permissions at all
	const hasAnySurveyPermissions = () => {
		if (!surveyPermissions) return false;

		// Check top-level read permission
		if (surveyPermissions.read) return true;

		// Check each survey type
		return (
			canReadEventRegistrationSurvey() ||
			canReadSatisfactionSurvey() ||
			canReadCustomSurvey() ||
			canReadDonationSurvey()
		);
	};

	return {
		surveyPermissions,
		// Generic checkers
		canReadSurveyType,
		canWriteSurveyType,
		canViewReportingSurveyType,
		// Specific survey type checkers
		canReadEventRegistrationSurvey,
		canReadSatisfactionSurvey,
		canReadCustomSurvey,
		canReadDonationSurvey,
		canWriteEventRegistrationSurvey,
		canWriteSatisfactionSurvey,
		canWriteCustomSurvey,
		canWriteDonationSurvey,
		canViewReportingEventRegistrationSurvey,
		canViewReportingSatisfactionSurvey,
		canViewReportingCustomSurvey,
		canViewReportingDonationSurvey,
		// Overall check
		hasAnySurveyPermissions,
	};
};
